import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useMeasure, useMountedState } from 'react-use'
import styled from 'styled-components'
import { fabric } from 'fabric'

import { useCanvas } from '@/hooks/useCanvas'
import {
  MODEL_POSITION,
  ORIENTATION,
  Orientation,
} from '@/constants/tools/solid-background-video'
import { Model } from '@/types/models'

type CanvasProps = {
  orientation: Orientation
  backgroundColor: string | null
  model?: Model
  modelPosition: {
    left: number
    top: number
    scaleX: number
    scaleY: number
  }
  onChangeModelPosition: ({}: {
    left: number
    top: number
    scaleX: number
    scaleY: number
  }) => void
}

export const Canvas: FC<CanvasProps> = ({
  orientation,
  backgroundColor,
  model,
  modelPosition,
  onChangeModelPosition,
}) => {
  const isMounted = useMountedState()

  if (!isMounted()) {
    return null
  }

  if (!model) {
    return null
  }

  return (
    <CanvasImpl
      orientation={orientation}
      backgroundColor={backgroundColor}
      model={model}
      modelPosition={modelPosition}
      onChangeModelPosition={onChangeModelPosition}
    />
  )
}

const CanvasImpl: FC<Required<CanvasProps>> = ({
  orientation,
  backgroundColor,
  model,
  modelPosition,
  onChangeModelPosition,
}) => {
  const modelImageSrc = model.clothes[0].deployImage.edit_src

  const [canvasRef, setCanvasRef] = useCanvas((canvas) => {
    canvas.selection = false
  })

  const [modelFabricObject, setModelFabricObject] = useState<fabric.Image>()

  const originalWidth = orientation === ORIENTATION.PORTRAIT ? 1080 : 1920
  const originalHeight = orientation === ORIENTATION.PORTRAIT ? 1920 : 1080

  const [containerRef, { width: containerWidth, height: containerHeight }] =
    useMeasure()

  const { width: fixedContainerWidth, height: fixedContainerHeight } = useMemo(
    () => ({
      width: containerWidth || 1,
      height: containerHeight || 1,
    }),
    [containerWidth, containerHeight],
  )

  /**
   * 캔버스가 그려지는 영역의 크기에 따라 캔버스의 크기 조정
   */
  useEffect(() => {
    const scaleWidth = fixedContainerWidth / originalWidth
    const scaleHeight = fixedContainerHeight / originalHeight

    const scale = Math.min(scaleWidth, scaleHeight)

    canvasRef.current?.setDimensions({
      width: originalWidth * scale,
      height: originalHeight * scale,
    })
    canvasRef.current?.setZoom(scale)
    canvasRef.current?.renderAll()
  }, [
    fixedContainerWidth,
    fixedContainerHeight,
    originalWidth,
    originalHeight,
    canvasRef,
  ])

  /**
   * 배경색 변경
   */
  const setBackgroundColor = useCallback(
    async (color: string | null) => {
      if (!canvasRef.current) {
        return
      }

      return new Promise<void>((resolve) => {
        canvasRef.current?.setBackgroundColor(color || 'transparent', () => {
          canvasRef.current?.renderAll()
          resolve()
        })
      })
    },
    [canvasRef],
  )

  useEffect(() => {
    setBackgroundColor(backgroundColor)
  }, [backgroundColor, setBackgroundColor, canvasRef])

  /**
   * 모델의 변경 이벤트 핸들러
   */
  const handleModifiedModelObject = useCallback(
    (e: fabric.IEvent) => {
      if (!e.target) {
        return
      }

      const { left, top, scaleX, scaleY } = e.target

      if (
        typeof left !== 'number' ||
        typeof top !== 'number' ||
        typeof scaleX !== 'number' ||
        typeof scaleY !== 'number'
      ) {
        return
      }

      onChangeModelPosition({
        left: left,
        top: top,
        scaleX,
        scaleY,
      })
    },
    [onChangeModelPosition],
  )

  /**
   * 모델의 생성하여 캔버스에 삽입\
   * 모델의 변경 이벤트 핸들러 등록
   */
  useEffect(() => {
    if (!canvasRef.current) {
      return
    }

    let createdModelFabricObject = modelFabricObject

    if (!createdModelFabricObject) {
      createdModelFabricObject = new fabric.Image('', {
        selectable: false,
        evented: false,
        lockScalingFlip: true,
        lockSkewingX: true,
        lockSkewingY: true,
        lockRotation: true,
        srcFromAttribute: true,
        hasRotatingPoint: false,
      })

      createdModelFabricObject.setControlsVisibility({
        tl: true,
        tr: true,
        br: true,
        bl: true,
        ml: false,
        mt: false,
        mr: false,
        mb: false,
        mtr: false,
      })

      setModelFabricObject(createdModelFabricObject)
    }

    createdModelFabricObject.off('modified')
    createdModelFabricObject.on('modified', handleModifiedModelObject)

    createdModelFabricObject.setSrc(modelImageSrc, () => {
      canvasRef.current?.renderAll()
    })

    canvasRef.current.remove(createdModelFabricObject)
    canvasRef.current.add(createdModelFabricObject)
  }, [modelImageSrc, canvasRef, modelFabricObject, handleModifiedModelObject])

  /**
   * 캔버스의 방향이 바뀌면 모델의 포지션을 조정
   */
  useEffect(() => {
    const position =
      orientation === ORIENTATION.PORTRAIT
        ? MODEL_POSITION.PORTRAIT
        : MODEL_POSITION.LANDSCAPE

    onChangeModelPosition({
      left: position.left,
      top: position.top,
      scaleX: position.scaleX,
      scaleY: position.scaleY,
    })
  }, [orientation, onChangeModelPosition])

  /**
   * 모델의 포지션이 바뀌면 캔버스에 반영
   */
  useEffect(() => {
    if (!canvasRef.current) {
      return
    }

    if (!modelFabricObject) {
      return
    }

    modelFabricObject.set({
      left: modelPosition.left,
      top: modelPosition.top,
      scaleX: modelPosition.scaleX,
      scaleY: modelPosition.scaleY,
    })

    canvasRef.current.renderAll()
  }, [modelPosition, canvasRef, modelFabricObject])

  return (
    <CanvasContainer
      ref={(containerEl) => !!containerEl && containerRef(containerEl)}
    >
      <CanvasWrapper>
        <canvas ref={setCanvasRef} />
      </CanvasWrapper>
    </CanvasContainer>
  )
}

const CanvasContainer = styled.div`
  display: flex;

  height: 100%;
  position: relative;
  overflow: auto;
`

const CanvasWrapper = styled.div`
  position: relative;
  margin: auto;

  border-radius: ${({ theme }) => theme.borderRadius}px;
  overflow: hidden;
`
